import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Center,
  Checkbox,
  Collapse,
  Group,
  Modal,
  NativeSelect,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconCheck, IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import { useConsentTypes } from '../api/consent';
import { useOrgs } from '../api/orgs';
import { useProducts } from '../api/products';
import {
  usePublishWorkflow,
  useUpdateWorkflow,
  useValidateWorkflow,
  useWorkflow,
  type WorkflowDefinitionGraph,
} from '../api/workflows';
import { definitionsEqual } from './workflow-edit-helpers';
import { WorkflowEditorCanvasArea } from './workflow-editor-canvas-area';

const STATUS_COLOURS: Record<string, string> = {
  draft: 'yellow',
  active: 'green',
  archived: 'gray',
};

export function WorkflowEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const query = useWorkflow(id);
  const update = useUpdateWorkflow(id ?? '');
  const validate = useValidateWorkflow(id ?? '');
  const publish = usePublishWorkflow(id ?? '');

  // code → blocking map for the workflow's product, threaded into the
  // canvas node labels so consent-gate boxes render `code · blocking`
  // / `code · optional`. Optional: before consents load the box shows
  // the bare code (graceful).
  const consentTypes = useConsentTypes(query.data?.productId ?? undefined);
  const consentBlockingByCode = useMemo(
    () => new Map((consentTypes.data ?? []).map((c) => [c.code, c.blocking])),
    [consentTypes.data],
  );

  const [definition, setDefinition] = useState<WorkflowDefinitionGraph | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Currently-selected step id, drives the right side panel. Cleared
  // on pane click; preserved across edits to the same step (the panel
  // edits the definition, which keeps the id stable until the operator
  // explicitly renames).
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Fullscreen modal toggle for the canvas area, so operators can work
  // on large workflows without the page chrome eating vertical space.
  // Instant (no transition, see the Modal below) — the toggle just pops,
  // which also means there's no async exit window where both the inline
  // and fullscreen canvases could briefly be mounted at once.
  const [fullscreen, { open: openFullscreen, close: closeFullscreen }] = useDisclosure(false);
  // Validation errors from the most recent Save / Publish attempt.
  // Empty array means the saved definition is publish-ready; null
  // means validate hasn't run yet for the current saved state.
  const [validationErrors, setValidationErrors] = useState<string[] | null>(null);
  // Non-blocking validator findings (e.g. a run_ai_review step with no
  // on_failure transition) — shown alongside errors but never block publish.
  const [validationWarnings, setValidationWarnings] = useState<string[]>([]);
  const [publishError, setPublishError] = useState<string | null>(null);

  const serverDefinition = query.data?.definition as WorkflowDefinitionGraph | null | undefined;

  // Initialise / replace the local definition when the server payload
  // changes (initial load, or after a successful save where the
  // mutation's onSuccess updates the cache).
  useEffect(() => {
    if (!serverDefinition) return;
    setDefinition((prev) =>
      prev && definitionsEqual(prev, serverDefinition) ? prev : serverDefinition,
    );
  }, [serverDefinition]);

  const dirty = !!definition && !definitionsEqual(definition, serverDefinition);

  // Browser-level "you have unsaved changes" guard.
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  function handleSave() {
    if (!definition) return;
    setSaveError(null);
    setValidationErrors(null);
    setValidationWarnings([]);
    update.mutate(
      { definition },
      {
        onSuccess: () => {
          notifications.show({ color: 'green', message: 'Draft saved.' });
          // Run validate against the freshly-saved definition so the
          // operator sees a heads-up about anything that would block
          // a publish, without having to click Publish first.
          validate.mutate(undefined, {
            onSuccess: (r) => {
              setValidationErrors(r.errors);
              setValidationWarnings(r.warnings);
            },
            onError: () => {
              // Validate failures are non-blocking; the operator can
              // still keep editing. Just log to the console — the
              // Save+notification was the user-visible action.
              setValidationErrors(null);
              setValidationWarnings([]);
            },
          });
        },
        onError: (err) => {
          if (err instanceof ApiError) {
            const parsed = (() => {
              try {
                return JSON.parse(err.body) as { message?: string };
              } catch {
                return null;
              }
            })();
            setSaveError(parsed?.message ?? `Save failed (${err.status})`);
            return;
          }
          setSaveError(err instanceof Error ? err.message : 'Save failed');
        },
      },
    );
  }

  function handlePublish() {
    if (dirty) {
      setPublishError('Save your draft first — Publish acts on the saved definition.');
      return;
    }
    setPublishError(null);
    publish.mutate(undefined, {
      onSuccess: () => {
        notifications.show({ color: 'green', message: 'Workflow published.' });
        // Bounce back to the read-only detail page; the editor's job
        // is done and "edit a published row" goes through the
        // new-version flow on the detail page.
        if (id) navigate(`/workflows/${id}`);
      },
      onError: (err) => {
        if (err instanceof ApiError) {
          const parsed = (() => {
            try {
              return JSON.parse(err.body) as { message?: string; errors?: string[] };
            } catch {
              return null;
            }
          })();
          if (parsed?.errors?.length) {
            setValidationErrors(parsed.errors);
            setPublishError('Publish refused — fix the validation errors below.');
            return;
          }
          setPublishError(parsed?.message ?? `Publish failed (${err.status})`);
          return;
        }
        setPublishError(err instanceof Error ? err.message : 'Publish failed');
      },
    });
  }

  if (query.isPending) {
    return (
      <Center mih={300}>
        <Text c="dimmed">Loading…</Text>
      </Center>
    );
  }
  if (query.isError || !query.data) {
    return (
      <Alert color="red" icon={<IconAlertTriangle size={18} />}>
        Could not load workflow.
      </Alert>
    );
  }
  const w = query.data;

  const renderCanvasArea = (isFullscreen: boolean) =>
    definition && (
      <WorkflowEditorCanvasArea
        definition={definition}
        setDefinition={setDefinition}
        selectedId={selectedId}
        onSelect={setSelectedId}
        consentBlockingByCode={consentBlockingByCode}
        workflowOrgId={query.data?.orgId ?? null}
        workflowProductId={query.data?.productId ?? null}
        fullscreen={isFullscreen}
        onToggleFullscreen={isFullscreen ? closeFullscreen : openFullscreen}
      />
    );

  return (
    <>
      <Stack gap="md" h="calc(100vh - 6.5rem)">
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <div>
            <Anchor component={Link} to={`/workflows/${w.id}`} size="sm">
              ← Back to workflow
            </Anchor>
            <Title order={2}>{w.name}</Title>
          </div>
          <Group gap="xs">
            <Badge color={STATUS_COLOURS[w.status] ?? 'gray'} variant="light">
              {w.status}
            </Badge>
            <Badge variant="outline">v{w.version}</Badge>
            {dirty && (
              <Badge color="orange" variant="filled">
                unsaved
              </Badge>
            )}
            <Button
              variant="default"
              disabled={!dirty || update.isPending}
              loading={update.isPending}
              onClick={handleSave}
            >
              Save draft
            </Button>
            <Button
              disabled={dirty || publish.isPending || w.status !== 'draft'}
              loading={publish.isPending}
              onClick={handlePublish}
              title={
                w.status !== 'draft'
                  ? 'Already published'
                  : dirty
                    ? 'Save your draft first'
                    : undefined
              }
            >
              Publish
            </Button>
          </Group>
        </Group>

        <AssignmentCard workflow={w} update={update} />

        {saveError && (
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>
            {saveError}
          </Alert>
        )}
        {publishError && (
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>
            {publishError}
          </Alert>
        )}
        {validationErrors !== null && validationErrors.length > 0 && (
          <Alert
            color="yellow"
            icon={<IconAlertTriangle size={18} />}
            title={`${validationErrors.length} validation error${validationErrors.length === 1 ? '' : 's'} — fix to publish`}
          >
            <Stack gap={4}>
              {validationErrors.map((err, i) => (
                <Text key={i} size="sm" ff="monospace">
                  {err}
                </Text>
              ))}
            </Stack>
          </Alert>
        )}
        {validationWarnings.length > 0 && (
          <Alert
            color="blue"
            icon={<IconAlertTriangle size={18} />}
            title={`${validationWarnings.length} validation warning${validationWarnings.length === 1 ? '' : 's'} — won't block publish`}
          >
            <Stack gap={4}>
              {validationWarnings.map((warning, i) => (
                <Text key={i} size="sm" ff="monospace">
                  {warning}
                </Text>
              ))}
            </Stack>
          </Alert>
        )}
        {validationErrors !== null && validationErrors.length === 0 && (
          <Alert color="green" icon={<IconAlertTriangle size={18} />}>
            Definition passes validation — ready to publish.
          </Alert>
        )}

        {!fullscreen && renderCanvasArea(false)}
      </Stack>

      <Modal
        fullScreen
        opened={fullscreen}
        onClose={closeFullscreen}
        withCloseButton={false}
        padding="md"
        transitionProps={{ duration: 0 }}
      >
        <Stack gap="md" h="100vh">
          <Group justify="space-between" align="center">
            <Group gap="xs">
              <Title order={3}>{w.name}</Title>
              {dirty && (
                <Badge color="orange" variant="filled">
                  unsaved
                </Badge>
              )}
            </Group>
            <Group gap="xs">
              <Button
                variant="default"
                disabled={!dirty || update.isPending}
                loading={update.isPending}
                onClick={handleSave}
              >
                Save draft
              </Button>
              <Button
                disabled={dirty || publish.isPending || w.status !== 'draft'}
                loading={publish.isPending}
                onClick={handlePublish}
              >
                Publish
              </Button>
            </Group>
          </Group>
          {renderCanvasArea(true)}
        </Stack>
      </Modal>
    </>
  );
}

/**
 * Inline editor for the workflow's (org, product) assignment.
 * Editable only when status='draft' — the orchestrator rejects
 * reassignment on published/archived rows. When the slot changes,
 * the orchestrator renumbers `version` to the next free slot under
 * the new (org, product) so the operator doesn't have to think
 * about the unique constraint.
 */
function AssignmentCard({
  workflow,
  update,
}: {
  workflow: {
    orgId: string | null;
    productId: string | null;
    status: 'draft' | 'active' | 'archived';
  };
  update: ReturnType<typeof useUpdateWorkflow>;
}) {
  const orgs = useOrgs();
  const products = useProducts();
  const [orgId, setOrgId] = useState(workflow.orgId ?? '');
  const [productId, setProductId] = useState(workflow.productId ?? '');
  const [isDefault, setIsDefault] = useState(workflow.orgId === null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [opened, { toggle }] = useDisclosure(true);

  useEffect(() => {
    setOrgId(workflow.orgId ?? '');
    setProductId(workflow.productId ?? '');
    setIsDefault(workflow.orgId === null);
  }, [workflow.orgId, workflow.productId]);

  const dirty =
    isDefault !== (workflow.orgId === null) ||
    (!isDefault && orgId !== (workflow.orgId ?? '')) ||
    productId !== (workflow.productId ?? '');
  const isDraft = workflow.status === 'draft';

  function handleSave() {
    setError(null);
    if (!productId) {
      setError('Pick a product.');
      return;
    }
    if (!isDefault && !orgId) {
      setError('Pick an organisation, or tick "Default for all organisations".');
      return;
    }
    const payload = isDefault
      ? { is_default: true as const, product_id: productId }
      : { org_id: orgId, product_id: productId };
    update.mutate(payload, {
      onSuccess: () => {
        setSavedAt(Date.now());
        notifications.show({ color: 'green', message: 'Assignment updated.' });
      },
      onError: (err) => {
        if (err instanceof ApiError) {
          try {
            const parsed = JSON.parse(err.body) as { message?: string };
            setError(parsed.message ?? `Reassignment failed (${err.status})`);
            return;
          } catch {
            /* fall through */
          }
        }
        setError(err instanceof Error ? err.message : 'Reassignment failed');
      },
    });
  }

  const orgName =
    workflow.orgId === null
      ? 'Default — all orgs'
      : ((orgs.data ?? []).find((o) => o.id === workflow.orgId)?.name ?? workflow.orgId);
  const productName =
    (products.data ?? []).find((p) => p.id === workflow.productId)?.displayName ??
    workflow.productId;

  return (
    <Card withBorder padding="sm">
      <Stack gap="xs">
        <Group justify="space-between" align="center">
          <Group gap="xs">
            <Text fw={600} size="sm">
              Assignment
            </Text>
            {!opened && orgName && productName && (
              <Text size="xs" c="dimmed">
                {orgName} · {productName}
              </Text>
            )}
          </Group>
          <Group gap="xs">
            {!isDraft && (
              <Text size="xs" c="dimmed">
                Read-only — only draft workflows can be reassigned.
                {orgName && productName && ` Currently bound to ${orgName} · ${productName}.`}
              </Text>
            )}
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              aria-label={opened ? 'Collapse assignment' : 'Expand assignment'}
              onClick={toggle}
            >
              {opened ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
            </ActionIcon>
          </Group>
        </Group>

        <Collapse in={opened}>
          <Stack gap="xs">
            {isDraft ? (
              <>
                <Group gap="sm" wrap="wrap" align="flex-end">
                  <NativeSelect
                    label="Organisation"
                    value={isDefault ? '' : orgId}
                    disabled={isDefault}
                    onChange={(e) => setOrgId(e.currentTarget.value)}
                    data={[
                      { value: '', label: 'Select an organisation…' },
                      ...(orgs.data ?? []).map((o) => ({ value: o.id, label: o.name })),
                    ]}
                  />
                  <NativeSelect
                    label="Product"
                    value={productId}
                    onChange={(e) => setProductId(e.currentTarget.value)}
                    data={[
                      { value: '', label: 'Select a product…' },
                      ...(products.data ?? []).map((p) => ({ value: p.id, label: p.displayName })),
                    ]}
                  />
                  <Button
                    size="sm"
                    disabled={!dirty || update.isPending}
                    loading={update.isPending}
                    onClick={handleSave}
                    leftSection={
                      savedAt && !dirty && Date.now() - savedAt < 4000 ? (
                        <IconCheck size={14} />
                      ) : null
                    }
                  >
                    {dirty ? 'Save assignment' : 'Saved'}
                  </Button>
                </Group>
                <Checkbox
                  label="Default for all organisations"
                  description="Applies to every org with this product until they author their own override."
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.currentTarget.checked)}
                />
                {error && (
                  <Text size="xs" c="red">
                    {error}
                  </Text>
                )}
                <Text size="xs" c="dimmed">
                  Changing the (org, product) renumbers <code>version</code> to the next free value
                  under the new slot.
                </Text>
              </>
            ) : (
              <Group gap="md">
                <div>
                  <Text size="xs" c="dimmed">
                    Organisation
                  </Text>
                  <Text size="sm">{orgName ?? '—'}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">
                    Product
                  </Text>
                  <Text size="sm">{productName ?? '—'}</Text>
                </div>
              </Group>
            )}
          </Stack>
        </Collapse>
      </Stack>
    </Card>
  );
}
